import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Cookie, Sparkles, Copy, Check, Loader2, ArrowRight, Download } from "lucide-react";
import SEOHead from "../components/SEOHead";

const DEFAULT_PROCESSORS = [
  "Google Analytics", "Google Ads", "Meta Pixel", "Stripe", "Resend",
  "SendGrid", "HubSpot", "Hotjar", "Cloudflare", "Intercom",
];

/**
 * Legal Center: Privacy Policy Generator + Cookie Consent Banner.
 * Free tool to acquire leads, paid upgrade for unlimited + branding.
 */
export default function LegalCenterPage() {
  const { language } = useLanguage();
  const { user } = useAuth();

  // tab: "privacy" | "impressum" | "terms" | "cookies"
  const initial = typeof window !== "undefined"
    ? (new URLSearchParams(window.location.search).get("tab") || "privacy")
    : "privacy";
  const [tab, setTab] = useState(initial);

  return (
    <div
      className="min-h-screen themed-section"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
      data-testid="legal-center-page"
    >
      <SEOHead
        slug="legal-center"
        title="Free Privacy Policy Generator + Cookie Consent Banner | TopRankPulse"
        description="Generate a GDPR-compliant Privacy Policy in 26 languages + install a cookie consent banner in 2 minutes. Free. No credit card. Used by 10k+ websites."
      />

      {/* Top bar */}
      <div
        className="sticky top-0 z-40 backdrop-blur border-b"
        style={{
          background: "color-mix(in srgb, var(--color-bg) 80%, transparent)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-black text-xl">
            <span style={{ color: "var(--color-primary)" }}>Top</span>RankPulse
          </Link>
          {user?.email ? (
            <Link
              to="/dashboard?tab=legal"
              className="px-3 py-1.5 text-xs font-bold rounded-lg"
              style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
              data-testid="nav-my-legal"
            >
              My Legal Docs →
            </Link>
          ) : (
            <Link
              to="/login"
              className="px-3 py-1.5 text-xs font-bold rounded-lg"
              style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
            >
              Sign in
            </Link>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 pt-14 pb-6 text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
          style={{
            background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
            color: "var(--color-primary)",
          }}
        >
          <Shield className="w-3.5 h-3.5" />
          <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
            GDPR · ePrivacy · CCPA
          </span>
        </div>
        <h1
          className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-4"
          style={{ color: "var(--color-text)" }}
        >
          Legal Center.{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
            }}
          >
            100% free.
          </span>
        </h1>
        <p className="text-lg max-w-2xl mx-auto" style={{ color: "var(--color-text-muted)" }}>
          Privacy Policy in 26 languages + a lightweight cookie consent banner you can embed in 2 minutes.
          No Cookiebot. No Iubenda. No €20/mo.
        </p>
      </section>

      {/* Tabs */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12">
        <div
          data-testid="legal-tab-switcher"
          className="flex items-center p-1 rounded-full w-fit mx-auto mb-8"
          style={{ background: "var(--color-surface)" }}
        >
          {[
            { id: "privacy", label: "Privacy Policy", icon: Shield },
            { id: "impressum", label: "Impressum", icon: Shield },
            { id: "terms", label: "Terms of Service", icon: Shield },
            { id: "cookies", label: "Cookie Banner", icon: Cookie },
          ].map((it) => (
            <button
              key={it.id}
              onClick={() => setTab(it.id)}
              data-testid={`legal-tab-${it.id}`}
              className="px-4 py-2 text-xs font-bold rounded-full flex items-center gap-1.5 transition-all"
              style={{
                background: tab === it.id ? "var(--color-primary)" : "transparent",
                color: tab === it.id ? "var(--color-bg)" : "var(--color-text-muted)",
              }}
            >
              <it.icon className="w-3.5 h-3.5" />
              {it.label}
            </button>
          ))}
        </div>

        {tab === "privacy" && <DocumentForm user={user} language={language} kind="privacy_policy" />}
        {tab === "impressum" && <DocumentForm user={user} language={language} kind="impressum" />}
        {tab === "terms" && <DocumentForm user={user} language={language} kind="terms" />}
        {tab === "cookies" && <CookieBannerForm user={user} language={language} />}
      </section>

      <footer
        className="py-10 text-center text-xs border-t mt-16"
        style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)" }}
      >
        <Link to="/" className="hover:opacity-80">← Back to TopRankPulse</Link>
      </footer>
    </div>
  );
}

// ---------- Generic Document Form (Privacy / Impressum / Terms) ----------
const DOC_META = {
  privacy_policy: { label: "Privacy Policy", cta: "Generate Privacy Policy" },
  impressum: { label: "Impressum / Legal Notice", cta: "Generate Impressum" },
  terms: { label: "Terms of Service", cta: "Generate Terms of Service" },
};

function DocumentForm({ user, language, kind }) {
  const meta = DOC_META[kind] || DOC_META.privacy_policy;
  const [form, setForm] = useState({
    company_name: "",
    country: "Slovakia",
    website_url: "",
    contact_email: user?.email || "",
    collects_personal_data: true,
    uses_cookies: true,
    data_processors: kind === "privacy_policy" ? ["Google Analytics"] : [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [result, setResult] = useState(null);

  // Reset result when switching kinds
  useEffect(() => {
    setResult(null);
    setErr(null);
    setLimitInfo(null);
  }, [kind]);

  const toggleProcessor = (p) => {
    setForm((f) => ({
      ...f,
      data_processors: f.data_processors.includes(p)
        ? f.data_processors.filter((x) => x !== p)
        : [...f.data_processors, p],
    }));
  };

  const generate = async () => {
    if (!form.company_name.trim() || !form.website_url.trim() || !form.contact_email.trim()) {
      setErr("Please fill Company, Website URL, and Contact email");
      return;
    }
    setBusy(true);
    setErr(null);
    setLimitInfo(null);
    setResult(null);
    try {
      const r = await axios.post(`${API}/legal/generate-document`, {
        kind,
        ...form,
        language,
        user_email: user?.email || null,
      });
      setResult(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && typeof detail === "object") {
        setLimitInfo(detail);
      } else {
        setErr(typeof detail === "string" ? detail : "Generation failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-5 gap-6">
      {/* Form */}
      <div
        className="md:col-span-2 rounded-2xl border p-5 h-fit"
        style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
      >
        <p className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: "var(--color-text-muted)" }}>
          Company details · {meta.label}
        </p>
        <div className="space-y-3">
          {[
            ["company_name", "Company name", "Acme s.r.o."],
            ["country", "Country", "Slovakia"],
            ["website_url", "Website URL", "https://yourdomain.com"],
            ["contact_email", "Contact email", "legal@yourdomain.com"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1"
                style={{ color: "var(--color-text-muted)" }}
              >
                {label}
              </label>
              <input
                type="text"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={ph}
                data-testid={`${kind}-${k}`}
                className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none"
                style={{
                  background: "var(--color-surface-alt)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>
          ))}

          {kind === "privacy_policy" && (
            <>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-wider mb-2"
                  style={{ color: "var(--color-text-muted)" }}
                >
                  Third-party services you use
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {DEFAULT_PROCESSORS.map((p) => {
                    const active = form.data_processors.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => toggleProcessor(p)}
                        data-testid={`proc-${p.replace(/\s/g, "_")}`}
                        className="px-2.5 py-1 text-[11px] font-bold rounded-full border transition-colors"
                        style={{
                          background: active ? "var(--color-primary)" : "transparent",
                          color: active ? "var(--color-bg)" : "var(--color-text-muted)",
                          borderColor: active ? "var(--color-primary)" : "var(--color-border)",
                        }}
                      >
                        {active && "✓ "}{p}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm mt-2" style={{ color: "var(--color-text)" }}>
                <input
                  type="checkbox"
                  checked={form.uses_cookies}
                  onChange={(e) => setForm({ ...form, uses_cookies: e.target.checked })}
                  data-testid={`${kind}-uses-cookies`}
                />
                My site uses cookies
              </label>
            </>
          )}

          <button
            onClick={generate}
            disabled={busy}
            data-testid={`${kind}-generate-btn`}
            className="w-full mt-3 px-4 py-3 font-bold rounded-lg flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
            style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? "Generating..." : meta.cta}
          </button>

          {err && <p className="text-xs" style={{ color: "#ef4444" }}>{err}</p>}
          {limitInfo && (
            <div
              data-testid="legal-limit-banner"
              className="text-xs p-3 rounded-lg border-2"
              style={{
                borderColor: "var(--color-primary)",
                background: "color-mix(in srgb, var(--color-primary) 8%, transparent)",
                color: "var(--color-text)",
              }}
            >
              🔒 {limitInfo.message}
              <Link
                to="/#pricing"
                className="block mt-2 font-bold"
                style={{ color: "var(--color-primary)" }}
                data-testid="legal-limit-upgrade"
              >
                Upgrade to PRO →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Output */}
      <div className="md:col-span-3">
        {!result ? (
          <div
            className="rounded-2xl border p-8 h-full flex flex-col items-center justify-center text-center"
            style={{ background: "var(--color-surface-alt)", borderColor: "var(--color-border)" }}
          >
            <Shield className="w-10 h-10 mb-3" style={{ color: "var(--color-text-muted)" }} />
            <p className="font-bold" style={{ color: "var(--color-text)" }}>Your {meta.label} will appear here</p>
            <p className="text-xs mt-2" style={{ color: "var(--color-text-muted)" }}>
              GPT-generated · HTML-ready · {language.toUpperCase()}
            </p>
          </div>
        ) : (
          <PolicyResult result={result} kind={kind} />
        )}
      </div>
    </div>
  );
}

// Keep alias for backwards compatibility
function PrivacyPolicyForm(props) {
  return <DocumentForm {...props} kind="privacy_policy" />;
}


function PolicyResult({ result, kind }) {
  const [copied, setCopied] = useState(false);
  const docLabel = kind === "impressum" ? "Impressum" : kind === "terms" ? "Terms of Service" : "Privacy Policy";
  const slug = kind === "impressum" ? "impressum" : kind === "terms" ? "terms" : "privacy-policy";
  const copy = () => {
    navigator.clipboard.writeText(result.html);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  const download = () => {
    const blob = new Blob(
      [`<!DOCTYPE html>\n<html><head><meta charset="utf-8"><title>${docLabel} · ${result.company_name}</title></head><body>\n${result.html}\n</body></html>`],
      { type: "text/html" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}-${result.doc_id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      data-testid={kind === "privacy_policy" ? "privacy-result" : `${kind}-result`}
      className="rounded-2xl border overflow-hidden"
      style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
    >
      <div
        className="px-4 py-3 flex items-center justify-between border-b"
        style={{ borderColor: "var(--color-border)" }}
      >
        <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: "var(--color-primary)" }}>
          ✓ Generated · {result.word_count} words
        </p>
        <div className="flex gap-2">
          <button
            onClick={copy}
            data-testid={`${kind}-copy-btn`}
            className="text-xs font-bold px-3 py-1.5 rounded border flex items-center gap-1"
            style={{ borderColor: "var(--color-border)", color: "var(--color-text)" }}
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? "Copied!" : "Copy HTML"}
          </button>
          <button
            onClick={download}
            data-testid={`${kind}-download-btn`}
            className="text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1"
            style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
          >
            <Download className="w-3 h-3" />
            Download .html
          </button>
        </div>
      </div>
      <div
        className="p-5 prose prose-sm max-w-none overflow-y-auto"
        style={{ maxHeight: 560, color: "var(--color-text)", background: "var(--color-surface-alt)" }}
        dangerouslySetInnerHTML={{ __html: result.html }}
      />
    </div>
  );
}


// ---------- Cookie Banner Form ----------
function CookieBannerForm({ user }) {
  const [form, setForm] = useState({
    site_name: "",
    site_url: "",
    language: "en",
    accent_color: "#6366f1",
    position: "bottom",
    style: "modern",
    categories: ["necessary", "analytics", "marketing", "functional"],
    policy_url: "",
  });
  const [created, setCreated] = useState(null);
  const [snippet, setSnippet] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    if (!user?.email) return;
    axios.get(`${API}/legal/consent/my-sites?email=${encodeURIComponent(user.email)}`)
      .then((r) => setSites(r.data.sites || []))
      .catch(() => {});
  }, [user?.email]);

  const create = async () => {
    if (!user?.email) {
      window.location.href = "/login?next=/legal-center";
      return;
    }
    if (!form.site_name.trim() || !form.site_url.trim()) {
      setErr("Enter site name and URL");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/legal/consent/create`, {
        ...form,
        user_email: user.email,
        policy_url: form.policy_url || null,
      });
      setCreated(r.data);
      const s = await axios.get(`${API}/legal/consent/${r.data.site_id}/embed`);
      setSnippet(s.data);
      setSites((list) => [r.data, ...list]);
    } catch (e) {
      setErr(e.response?.data?.detail || "Creation failed");
    } finally {
      setBusy(false);
    }
  };

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Config */}
      <div
        className="rounded-2xl border p-5 h-fit"
        style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
      >
        <p className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: "var(--color-text-muted)" }}>
          Configure your banner
        </p>
        <div className="space-y-3">
          {[
            ["site_name", "Site name", "Acme"],
            ["site_url", "Site URL", "https://acme.com"],
            ["policy_url", "Privacy Policy URL (optional)", "https://acme.com/privacy"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--color-text-muted)" }}>
                {label}
              </label>
              <input
                type="text"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={ph}
                data-testid={`cookie-${k}`}
                className="w-full px-3 py-2 text-sm rounded-lg border focus:outline-none"
                style={{
                  background: "var(--color-surface-alt)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>
          ))}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--color-text-muted)" }}>
                Accent color
              </label>
              <input
                type="color"
                value={form.accent_color}
                onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                data-testid="cookie-accent"
                className="w-full h-10 rounded-lg border cursor-pointer"
                style={{ borderColor: "var(--color-border)" }}
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--color-text-muted)" }}>
                Position
              </label>
              <select
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                data-testid="cookie-position"
                className="w-full h-10 rounded-lg border text-sm px-2"
                style={{ background: "var(--color-surface-alt)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              >
                <option value="bottom">Bottom</option>
                <option value="top">Top</option>
              </select>
            </div>
          </div>

          <button
            onClick={create}
            disabled={busy}
            data-testid="cookie-create-btn"
            className="w-full mt-3 px-4 py-3 font-bold rounded-lg flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
            style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cookie className="w-4 h-4" />}
            {busy ? "Creating..." : "Generate snippet"}
          </button>

          {err && <p className="text-xs" style={{ color: "#ef4444" }}>{err}</p>}
        </div>

        {sites.length > 0 && (
          <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--color-border)" }}>
            <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "var(--color-text-muted)" }}>
              Your existing sites
            </p>
            <div className="space-y-1">
              {sites.slice(0, 4).map((s) => (
                <div key={s.site_id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-bold truncate" style={{ color: "var(--color-text)" }}>{s.site_name}</span>
                  <span style={{ color: "var(--color-text-muted)" }}>{s.consents_logged || 0} consents</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Output */}
      <div>
        {!created ? (
          <div
            className="rounded-2xl border p-8 h-full flex flex-col items-center justify-center text-center"
            style={{ background: "var(--color-surface-alt)", borderColor: "var(--color-border)" }}
          >
            <Cookie className="w-10 h-10 mb-3" style={{ color: "var(--color-text-muted)" }} />
            <p className="font-bold" style={{ color: "var(--color-text)" }}>Your copy-paste snippet will appear here</p>
            <p className="text-xs mt-2" style={{ color: "var(--color-text-muted)" }}>
              Paste into your &lt;head&gt; and the banner appears on all pages.
            </p>
          </div>
        ) : (
          <div
            data-testid="cookie-snippet-result"
            className="rounded-2xl border overflow-hidden"
            style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
          >
            <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: "var(--color-border)" }}>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: "var(--color-primary)" }}>
                  ✓ Site ID · {created.site_id}
                </p>
                <p className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                  Paste into your &lt;head&gt;
                </p>
              </div>
              <button
                onClick={copySnippet}
                data-testid="cookie-copy-btn"
                className="text-xs font-bold px-3 py-1.5 rounded flex items-center gap-1"
                style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "Copied!" : "Copy snippet"}
              </button>
            </div>
            <pre
              className="p-4 text-[11px] font-mono overflow-auto"
              style={{ maxHeight: 420, background: "var(--color-surface-alt)", color: "var(--color-text)" }}
            >
              {snippet}
            </pre>
            <div className="px-4 py-3 border-t text-xs" style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}>
              <p className="flex items-center gap-1">
                <ArrowRight className="w-3 h-3" style={{ color: "var(--color-primary)" }} />
                Consents logged at{" "}
                <code className="font-mono" style={{ color: "var(--color-primary)" }}>
                  /api/legal/consent/log
                </code>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
